import { errorMsg } from "@material-dev/insights-portal-ui-components";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  ApiException,
  CreateProjectFromEncodedCommand,
  IProjectsClient,
  PageContextOfProjectFilter,
  ProjectsClient,
} from "app/ApiClient";
import { IState } from "slices";
import { updateProject } from "slices/project/projectSlice";
import { resetCompositeProjectMatadata } from "slices/projectsData/projectsDataSlice";
import { LoadingState } from "types";

import { originProjectsState, ProjectsFilterType, ProjectsState } from "./projectsState";

const projectsClient: IProjectsClient = new ProjectsClient(process.env.REACT_APP_API_URL);

export const getProjectsPage = createAsyncThunk("projects/getAll", async (_, { getState, rejectWithValue }) => {
  try {
    const { projects } = getState() as RootState;
    const result = await projectsClient.page(projects.pageContext);
    return result;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const createProjectFromEncoded = createAsyncThunk(
  "projects/createProjectFromEncoded",
  async (command: CreateProjectFromEncodedCommand, { dispatch }) => {
    try {
      const response = await projectsClient.createProjectFromEncoded(command);
      return response;
    } catch (error) {
      let parsedError: string;
      if (ApiException.isApiException(error)) {
        const { message, response } = error;
        const { errorMessage } = JSON.parse(response) as { errorMessage: string };
        parsedError = errorMessage || message;
      } else {
        const { message } = error as Error;
        parsedError = message;
      }
      errorMsg(parsedError);
    }
    dispatch(resetCompositeProjectMatadata());
  },
);

export const projectsSlice = createSlice({
  name: "projects",
  initialState: originProjectsState,
  reducers: {
    resetProjects: (state) => {
      state.projectsLoading = LoadingState.idle;
      state.data = undefined;
      state.error = undefined;
      state.pageContext = { ...originProjectsState.pageContext };
    },
    setProjectsPageContext: (state, { payload }: PayloadAction<PageContextOfProjectFilter>) => {
      state.pageContext = payload;
      state.filters = { ...state.filters };
    },
    setProjectsFilters: (state, { payload }: { payload: ProjectsFilterType }) => {
      if (!state.pageContext.filter) state.pageContext.filter = {};
      const filterValues = payload.normalizedDataSource?.map((x) => x || "").filter((x) => x !== "");
      if (filterValues && filterValues.length > 0)
        state.pageContext.filter.dataSource = {
          values: filterValues,
        };
      else state.pageContext.filter = undefined;

      state.filters = { ...state.filters, ...payload };
    },
    updateProjectName: (
      state,
      { payload: { projectId, projectName } }: { payload: { projectId: number; projectName: string } },
    ) => {
      if (state.data) {
        const project = state.data.pageData?.find((x) => x.id == projectId);
        if (project) project.name = projectName;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateProject.fulfilled, (state, action) => {
      state.data = state.data
        ? {
            ...state.data,
            pageData: state.data?.pageData?.map((item) =>
              item.originProjectId === action.meta.arg.originProjectId
                ? {
                    ...item,
                    id: action.meta.arg.command.projectId,
                    name: action.meta.arg.command.name,
                    versionId: action.meta.arg.command.encodedVersionId,
                  }
                : item,
            ),
          }
        : undefined;
    });

    builder.addCase(getProjectsPage.pending, (state) => {
      state.projectsLoading = LoadingState.pending;
    });
    builder.addCase(getProjectsPage.rejected, (state, action) => {
      state.projectsLoading = LoadingState.failed;
      state.error = action.error.message;
    });
    builder.addCase(getProjectsPage.fulfilled, (state, action) => {
      state.projectsLoading = LoadingState.succeed;
      state.data = action.payload;
    });

    // createProjectFromEncoded
    builder.addCase(createProjectFromEncoded.pending, (state, { meta: { arg } }) => {
      state.creatingFromEncodedOriginProjectId = arg.originProjectId;
    });
    builder.addCase(createProjectFromEncoded.rejected, (state) => {
      state.creatingFromEncodedOriginProjectId = null;
    });
    builder.addCase(createProjectFromEncoded.fulfilled, (state) => {
      state.creatingFromEncodedOriginProjectId = null;
    });
  },
});
export const { resetProjects, setProjectsPageContext, setProjectsFilters, updateProjectName } = projectsSlice.actions;

export const projectsSelector = (state: IState): ProjectsState => state.projects;

export default projectsSlice.reducer;
